"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { type OnboardingViewType } from "@/constants/onboarding";

// Re-export the type for backwards compatibility
export type { OnboardingViewType } from "@/constants/onboarding";

interface OnboardingModalContextValue {
  isOpen: boolean;
  initialStep: OnboardingViewType | null;
  openModal: (step?: OnboardingViewType) => void;
  closeModal: () => void;
}

const OnboardingModalContext = createContext<OnboardingModalContextValue | null>(
  null
);

interface OnboardingModalProviderProps {
  readonly children: ReactNode;
}

export function OnboardingModalProvider({
  children,
}: Readonly<OnboardingModalProviderProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const [initialStep, setInitialStep] = useState<OnboardingViewType | null>(
    null
  );

  const openModal = useCallback((step?: OnboardingViewType) => {
    setInitialStep(step ?? null);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setInitialStep(null);
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      initialStep,
      openModal,
      closeModal,
    }),
    [isOpen, initialStep, openModal, closeModal]
  );

  return (
    <OnboardingModalContext.Provider value={value}>
      {children}
    </OnboardingModalContext.Provider>
  );
}

export function useOnboardingModal(): OnboardingModalContextValue {
  const context = useContext(OnboardingModalContext);

  if (!context) {
    throw new Error(
      "useOnboardingModal must be used within an OnboardingModalProvider"
    );
  }

  return context;
}
